import classnames from 'classnames'
import React, { RefObject, useCallback, useImperativeHandle, useRef } from 'react'

import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { navigateToPDP } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import Product, { ProductLite } from '@src/quince-site-common/src/domains/Product'
import Bundle, { PRODUCT_TYPES } from '@src/quince-site-common/src/domains/Product/BundleProduct'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import { useBatchedAnalyticsCaptureForProductCard } from '@src/quince-site-common/src/hooks/useBatchedAnalyticsCaptureForProductCard'
import useDeviceInfo from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useIsVisible from '@src/quince-site-common/src/hooks/useIsVisible'
import { useProductCardTags } from '@src/quince-site-common/src/hooks/useProductCardTags'
import useRecurringItemSubscriptionsExperiment from '@src/quince-site-common/src/hooks/useRecurringItemSubscriptionsExperiment'
import { useProductDynamicData } from '@src/quince-site-common/src/redux/globalStateManager/productDynamicDataStateManager'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'

import { ProductCardHoverProvider } from './product-card-context/product-card-hover.context'
import { ProductCardContextProvider, useProductCardContext } from './product-card-context/product-card.context'
import { ProductCardFooter, ProductCardFooterProps } from './product-card-footer/product-card-footer'
import styles from './product-card.module.scss'
import { ProductImageSection, ProductImageSectionProps } from './product-image-section/product-image-section'
import { ProductTitleSection, ProductTitleSectionProps } from './product-title-section/product-title-section'

export type ProductCardProps = {
  product: Product | ProductLite | Bundle
  selectedOptions?: ProductVariantOption[] // TODO: Change this to accept Product Card directly
  className?: string
  searchQuery?: string
  position?: number
  shouldShowMore?: boolean
  optionLimit?: number
  wishlistTrackConfig?: Record<string, any>
  skipTagRefresh?: boolean
  onShowMoreClick?: (params: { selectedOptions: ProductVariantOption[] }) => void
  getTopLeftIconSVG?: (params: ProductVariantOption[]) => ((params: any) => JSX.Element) | null
  hideWishlistIcon?: boolean
  hideBuyNowIcon?: boolean
  hideTags?: boolean
  productViewTracker?: string
  nameAndPriceSectionClass?: string
  productCardImageSectionClass?: string
  showSecondImageOnProductTiles?: boolean
  showWhiteGloveDeliveryTag?: boolean
} & ProductImageSectionProps &
  ProductTitleSectionProps &
  ProductCardFooterProps

const ProductCardComponent = (props: ProductCardProps) => {
  const {
    getTopLeftIconSVG,
    hideWishlistIcon,
    hideProductName,
    productPriceClass,
    hideBuyNowIcon,
    productCardLinkClass,
    quality,
    hideTags,
    productPriceWrapperClass,
    productViewTracker,
    additionalAttributes,
    shouldShowMore = true,
    optionLimit = 4,
    showWhiteGloveDeliveryTag,
  } = props

  const { isNativeView } = useDeviceInfo()
  const router = useRouter()
  const wrapperRef: RefObject<HTMLDivElement> = useRef(null)
  const isRecurringItemSubscriptionsExpEnabled = useRecurringItemSubscriptionsExperiment()

  const { product, selectedOptions } = useProductCardContext()
  const spid = product.spid
  const { productTags, isWhiteGloveProduct } = useProductDynamicData({
    productId: product.id,
    productTags: product.tags,
    isProductConstructedOnServer: product.isConstructedOnServer,
    skipTagRefresh: props.skipTagRefresh,
    subscriptionExpEnabled: isRecurringItemSubscriptionsExpEnabled,
  })

  const { applicableTags, getItBy24 } = useProductCardTags({
    product,
    productTags: productTags ?? [],
    selectedOptions,
  })

  const onShowMoreClick = useCallback(
    ({ selectedOptions }: { selectedOptions: ProductVariantOption[] }) => {
      if (props.onShowMoreClick) {
        props.onShowMoreClick({ selectedOptions })
        return
      }
      console.log('>>>>> Old show more click called')

      navigateToPDP({
        product: product as Product | ProductLite,
        selectedOptions,
        additionalAttributes,
        tracker: '',
        router,
      })
    },
    [props.onShowMoreClick, product],
  )

  const topLeftIconSVG = getTopLeftIconSVG?.(selectedOptions)

  const { isVisible } = useIsVisible(wrapperRef)
  useBatchedAnalyticsCaptureForProductCard({
    applicableTags,
    product: product as Product,
    productViewTracker,
    selectedOptions,
    wrapperRef,
    isVisible,
  })

  const isProductWhiteGloveDelivery =
    isWhiteGloveProduct ??
    (props.product.type === PRODUCT_TYPES.NORMAL && !!props.product.isWhiteGloveDeliveryAvailable)

  const isLoggedIn = isBrowser() ? AuthUtil.isLoggedIn() : false

  return (
    <div className={classnames(styles.productCard)} ref={wrapperRef} data-testid="product-card">
      <ProductTitleSection
        priceClassName={props.priceClassName}
        productPrefixPriceClassName={props.productPrefixPriceClassName}
        titleClassName={props.titleClassName}
        additionalAttributes={props.additionalAttributes}
        productClickTrackConfig={props.productClickTrackConfig}
        selectorKey={props.selectorKey}
        tracker={props.tracker}
        hideProductName={hideProductName}
        productPriceClass={productPriceClass}
        productPriceWrapperClass={productPriceWrapperClass}
        productCardLinkClass={productCardLinkClass}
        nameAndPriceSectionClass={props.nameAndPriceSectionClass}
      />
      <ProductImageSection
        eagerLoadImage={props.eagerLoadImage}
        imageRef={props.imageRef}
        gender={props.gender}
        maxWidth={props.maxWidth}
        wishlistIconClassName={props.wishlistIconClassName}
        addToCartClassName={classnames(styles.buyItNowBtn, props.addToCartClassName)}
        deleteItemClassName={classnames(styles.deleteBtn, props.deleteItemClassName)}
        applicableTags={applicableTags}
        showDeleteBtn={props.showDeleteBtn}
        className={classnames(styles.productCardImageSection, props.productCardImageSectionClass)}
        spid={Number(spid)}
        wishlistTrackConfig={props.wishlistTrackConfig}
        topLeftIconSVG={topLeftIconSVG}
        hideWishlistIcon={(isNativeView && !isLoggedIn) || hideWishlistIcon}
        hideBuyNowIcon={isNativeView || hideBuyNowIcon}
        quality={quality}
        hideTags={hideTags}
        showSecondImageOnProductTiles={props.showSecondImageOnProductTiles}
        productClickTrackConfig={props.productClickTrackConfig}
      />
      <ProductCardFooter
        hideOptions={props.hideOptions}
        hideReviews={props.hideReviews}
        shouldShowMore={shouldShowMore}
        optionLimit={optionLimit}
        onShowMoreClick={onShowMoreClick}
        getItBy24={getItBy24}
        productViewTracker={productViewTracker}
        applicableTags={applicableTags}
        productClickTrackConfig={props.productClickTrackConfig}
        isWhiteGloveDelivery={showWhiteGloveDeliveryTag && isProductWhiteGloveDelivery}
      />
    </div>
  )
}

export const ProductCard = React.forwardRef<HTMLDivElement, ProductCardProps>(function ProductCardEl(props, ref) {
  const productCard = useRef<HTMLDivElement>(null)
  useImperativeHandle(ref, () => productCard.current as unknown as HTMLDivElement)

  return (
    <div ref={productCard} className={classnames(props.className)}>
      <ProductCardContextProvider
        product={props.product}
        initialSelectedOptions={props.selectedOptions}
        siteSearchQuery={props.searchQuery}
        productPosition={props.position}
      >
        <ProductCardHoverProvider>
          <ProductCardComponent {...props} />
        </ProductCardHoverProvider>
      </ProductCardContextProvider>
    </div>
  )
})
